import { Badge, BasePagination, HStack, IconButton, Text, Tooltip, VStack } from '@elkaso-app/web-design';
import { ICrmBrandsData, useGetCrmBrandsApi } from 'apis/crm-brands/use-get-crm-brands-api';
import { BaseSortButton, BaseTable, BaseTableContainer } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  TableHeader,
} from 'components/base-table/components';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import React, { useEffect, useState } from 'react';
import { FiEdit3, FiEye } from 'react-icons/fi';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { BrandNameFilterButton } from '../components/filters/brand-name-filter';

// Components for table cells
const BrandNameCell: React.FC<{
  name: string;
  image: string;
}> = ({ name, image }) => (
  <div style={{ height: '60px', display: 'flex', alignItems: 'center', padding: '8px 0' }}>
    <HStack spacing={3}>
      {image && (
        <div style={{ width: '40px', height: '40px', borderRadius: '8px', overflow: 'hidden' }}>
          <img
            src={image}
            alt={name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
      )}
      <VStack align='start' spacing={1}>
        <Text fontWeight='semibold' fontSize='md' color='gray.800' lineHeight='1.2'>
          {name}
        </Text>
      </VStack>
    </HStack>
  </div>
);

const PriceLevelCell: React.FC<{ priceLevel: number }> = ({ priceLevel }) => {
  const getPriceLevelInfo = (level: number) => {
    switch (level) {
      case 1:
        return { label: 'Budget', color: 'green' };
      case 2:
        return { label: 'Moderate', color: 'yellow' };
      case 3:
        return { label: 'Premium', color: 'orange' };
      case 4:
        return { label: 'Luxury', color: 'red' };
      default:
        return { label: 'Unknown', color: 'gray' };
    }
  };

  const { label, color } = getPriceLevelInfo(priceLevel);

  return (
    <div style={{ height: '60px', display: 'flex', alignItems: 'center', padding: '8px 0' }}>
      <Badge colorScheme={color as any} variant='subtle' size='md' borderRadius='md'>
        {label} ({priceLevel})
      </Badge>
    </div>
  );
};

const AvgPriceCell: React.FC<{ avgPricePerCouple: number }> = ({ avgPricePerCouple }) => (
  <div style={{ height: '60px', display: 'flex', alignItems: 'center', padding: '8px 0' }}>
    <Text fontWeight='medium' fontSize='md' color='gray.800'>
      AED {Math.round(avgPricePerCouple).toLocaleString()}
    </Text>
  </div>
);

const ActionsCell: React.FC<{
  brandId: number;
  brandName: string;
  onView: (brandId: number) => void;
  onEdit: (brandId: number) => void;
}> = ({ brandId, brandName, onView, onEdit }) => {
  return (
    <div
      style={{
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 0',
        gap: '8px',
      }}>
      <Tooltip label={`View ${brandName} details`}>
        <IconButton
          aria-label={`View ${brandName} details`}
          icon={<FiEye />}
          size='sm'
          colorScheme='blue'
          variant='outline'
          onClick={() => onView(brandId)}
        />
      </Tooltip>
      <Tooltip label={`Edit ${brandName}`}>
        <IconButton
          aria-label={`Edit ${brandName}`}
          icon={<FiEdit3 />}
          size='sm'
          colorScheme='green'
          variant='outline'
          onClick={() => onEdit(brandId)}
        />
      </Tooltip>
    </div>
  );
};

const CrmBrandsListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const dispatch = useAppDispatch();

  // State for search
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(debouncedSearchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [debouncedSearchTerm]);

  const { isLoading, isFetching, data } = useGetCrmBrandsApi();
  const { pageCount, total, data: brandsData } = data ?? {};

  // Filter data based on search
  const getFilteredData = () => {
    if (!brandsData) return [];

    if (debouncedSearchTerm) {
      return brandsData.filter((brand) => brand.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()));
    }

    return brandsData;
  };

  const filteredData = getFilteredData();

  const handleViewBrand = (brandId: number) => {
    // TODO: Implement view brand details functionality
    console.log('View brand:', brandId);
  };

  const handleEditBrand = (brandId: number) => {
    // TODO: Implement edit brand functionality
    console.log('Edit brand:', brandId);
  };

  // Set page title
  useEffect(() => {
    dispatch(changeTitle('CRM Brands'));
  }, [dispatch]);

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <TableHeader
            name='Brand'
            sortButtonComponent={<BaseSortButton sortBy='name' />}
            filterButtonComponent={<BrandNameFilterButton />}
          />
        ),
        accessor: 'brand',
        width: '40%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={BrandNameCell} {...value} />,
      },
      {
        Header: <TableHeader name='Price Level' />,
        accessor: 'priceLevel',
        width: '25%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={PriceLevelCell} {...value} />,
      },
      {
        Header: <TableHeader name='Avg. Price per Couple' />,
        accessor: 'avgPrice',
        width: '25%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={AvgPriceCell} {...value} />,
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        width: '10%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={ActionsCell} {...value} />,
      },
    ],
    []
  );

  const tableData = React.useMemo(
    () =>
      filteredData?.map((item: ICrmBrandsData) => ({
        id: item.id,
        brand: {
          name: item.name,
          image: item.image,
        },
        priceLevel: {
          priceLevel: item.priceLevel,
        },
        avgPrice: {
          avgPricePerCouple: item.avgPricePerCouple,
        },
        actions: {
          brandId: item.id,
          brandName: item.name,
          onView: handleViewBrand,
          onEdit: handleEditBrand,
        },
      })) || [],
    [filteredData]
  );

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='CRM Brands' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>

        <BaseTableContainer.Header.Right>
          <ClearFilterButton />
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      <BaseTableContainer.TableWrapper>
        <BaseTable columns={columns} data={tableData} />
      </BaseTableContainer.TableWrapper>

      <BaseTableContainer.Footer>
        <BaseTableContainer.Footer.Left />
        <BaseTableContainer.Footer.Right>
          <BasePagination>
            <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
            <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
          </BasePagination>
        </BaseTableContainer.Footer.Right>
      </BaseTableContainer.Footer>
    </BaseTableContainer>
  );
};

export default CrmBrandsListScreen;
